import { useClerk, useUser } from "@clerk/clerk-react";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { Menu, MessageCircle, Monitor, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import { Avatar, AvatarFallback, AvatarImage } from "#/components/ui/avatar";
import { Button } from "#/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "#/components/ui/dropdown-menu";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "#/components/ui/sheet";
import type { Theme } from "#/hooks/use-theme";
import { useTheme } from "#/hooks/use-theme";
import { useSignInModal } from "#/hooks/useSignInModal";
import i18n from "#/lib/i18n";

const THEME_OPTIONS = [
	{ value: "light" as Theme, icon: Sun, labelKey: "nav.themeLight" as const },
	{ value: "dark" as Theme, icon: Moon, labelKey: "nav.themeDark" as const },
	{
		value: "system" as Theme,
		icon: Monitor,
		labelKey: "nav.themeSystem" as const,
	},
];

function ThemeToggle() {
	const { theme, setTheme } = useTheme();
	const { t } = useTranslation();
	const current =
		THEME_OPTIONS.find((o) => o.value === theme) ?? THEME_OPTIONS[0];
	const Icon = current.icon;
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					aria-label={`${t("nav.theme")}: ${t(current.labelKey)}`}
				>
					<Icon className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{THEME_OPTIONS.map(({ value, icon: OptionIcon, labelKey }) => (
					<DropdownMenuItem
						key={value}
						onSelect={() => setTheme(value)}
						className={theme === value ? "font-medium text-foreground" : ""}
					>
						<OptionIcon className="mr-2 h-4 w-4" />
						{t(labelKey)}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function LanguageToggle() {
	const { i18n } = useTranslation();
	const isfi = i18n.resolvedLanguage === "fi";
	return (
		<Button
			variant="ghost"
			size="sm"
			className="text-[11px] font-bold tracking-widest text-muted-foreground hover:text-accent-foreground"
			onClick={() => {
				const next = isfi ? "en" : "fi";
				i18n.changeLanguage(next);
				localStorage.setItem("i18n-language", next);
			}}
		>
			{isfi ? "EN" : "FI"}
		</Button>
	);
}

const ADMIN_EMAIL = "samu.o.tiainen@gmail.com";

function MessagesLink() {
	const { t } = useTranslation();
	const { data: count = 0 } = useQuery(
		convexQuery(api.messages.unreadCount, {}),
	);
	return (
		<Link
			to="/messages"
			className="relative flex items-center text-muted-foreground transition-colors hover:text-foreground"
			aria-label={t("nav.messages")}
		>
			<MessageCircle className="h-5 w-5" />
			{count > 0 && (
				<span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary-accessible text-[10px] font-bold text-primary-foreground">
					{count > 9 ? "9+" : count}
				</span>
			)}
		</Link>
	);
}

function UserMenu() {
	const { t } = useTranslation();
	const { user } = useUser();
	const { signOut } = useClerk();
	const isAdmin = user?.primaryEmailAddress?.emailAddress === ADMIN_EMAIL;

	const initials =
		[user?.firstName?.[0], user?.lastName?.[0]]
			.filter(Boolean)
			.join("")
			.toUpperCase() || "?";

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="rounded-full">
					<Avatar>
						<AvatarImage
							src={user?.imageUrl}
							alt={user?.fullName ?? t("nav.messages")}
						/>
						<AvatarFallback>{initials}</AvatarFallback>
					</Avatar>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuGroup>
					<DropdownMenuItem asChild>
						<Link to="/my-listings">{t("nav.myListings")}</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link to="/watchlist">{t("nav.watchlist")}</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link to="/messages">{t("nav.messages")}</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link to="/settings" search={{ reason: undefined }}>
							{t("nav.settings")}
						</Link>
					</DropdownMenuItem>
					{isAdmin && (
						<DropdownMenuItem asChild>
							<Link to="/admin">{t("nav.admin")}</Link>
						</DropdownMenuItem>
					)}
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuItem onSelect={() => signOut()}>
					{t("nav.signOut")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function DesktopAuth() {
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { t } = useTranslation();
	const { open } = useSignInModal();

	if (isLoading) {
		return (
			<>
				<Button variant="outline" size="sm" disabled>
					{t("nav.signIn")}
				</Button>
				<Button size="sm" disabled>
					{t("nav.sellADisc")}
				</Button>
			</>
		);
	}

	if (!isAuthenticated) {
		return (
			<>
				<Button variant="outline" size="sm" onClick={open}>
					{t("nav.signIn")}
				</Button>
				<Button size="sm" disabled>
					{t("nav.sellADisc")}
				</Button>
			</>
		);
	}

	return (
		<>
			<MessagesLink />
			<Button size="sm" asChild>
				<Link to="/sell">{t("nav.sellADisc")}</Link>
			</Button>
			<UserMenu />
		</>
	);
}

const BROWSE_SEARCH = {
	sort: "newest" as "newest" | "price-asc" | "price-desc",
	q: "" as string,
	discClass: [] as string[],
	condition: [] as string[],
	color: [] as string[],
	shipsTo: [] as string[],
	country: [] as string[],
	plasticType: [] as string[],
	priceMin: "" as number | "",
	priceMax: "" as number | "",
	weightMin: "" as number | "",
	weightMax: "" as number | "",
	discModelIds: [] as string[],
	manufacturerIds: [] as string[],
	speedMin: "" as number | "",
	speedMax: "" as number | "",
	glideMin: "" as number | "",
	glideMax: "" as number | "",
	turnMin: "" as number | "",
	turnMax: "" as number | "",
	fadeMin: "" as number | "",
	fadeMax: "" as number | "",
	distanceKm: "" as number | "",
};

function MobileDrawer() {
	const { t } = useTranslation();
	const { user } = useUser();
	const { signOut } = useClerk();
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { open: openSignIn } = useSignInModal();
	const { data: unreadCount = 0 } = useQuery(
		convexQuery(api.messages.unreadCount, {}),
	);
	const [open, setOpen] = useState(false);
	const close = () => setOpen(false);

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					aria-label={t("nav.openMenu")}
					className="md:hidden"
				>
					<Menu className="h-5 w-5" />
				</Button>
			</SheetTrigger>
			<SheetContent side="right" className="w-72 px-0 pt-0">
				<SheetHeader className="border-b px-4 pb-4 pt-5">
					<SheetTitle className="display-title text-2xl tracking-[0.04em]">
						<span className="text-primary">RE:</span>DISC
					</SheetTitle>
				</SheetHeader>
				<nav className="flex flex-col gap-1 px-4 py-4">
					<Link
						to="/listings"
						search={BROWSE_SEARCH}
						className="flex items-center rounded-md px-2 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
						onClick={close}
					>
						{t("nav.browse")}
					</Link>
					{isAuthenticated ? (
						<>
							<Link
								to="/messages"
								className="flex items-center gap-2 rounded-md px-2 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
								onClick={close}
							>
								{t("nav.messages")}
								{unreadCount > 0 && (
									<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-accessible text-[10px] font-bold text-primary-foreground">
										{unreadCount > 9 ? "9+" : unreadCount}
									</span>
								)}
							</Link>
							<div className="px-2 py-1.5">
								<Button size="sm" className="w-full" asChild>
									<Link to="/sell" onClick={close}>
										{t("nav.sellADisc")}
									</Link>
								</Button>
							</div>
							<div className="my-2 border-t" />
							<p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
								{user?.firstName ?? t("nav.settings")}
							</p>
							<Link
								to="/my-listings"
								className="flex items-center rounded-md px-2 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
								onClick={close}
							>
								{t("nav.myListings")}
							</Link>
							<Link
								to="/watchlist"
								className="flex items-center rounded-md px-2 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
								onClick={close}
							>
								{t("nav.watchlist")}
							</Link>
							<Link
								to="/settings"
								search={{ reason: undefined }}
								className="flex items-center rounded-md px-2 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
								onClick={close}
							>
								{t("nav.settings")}
							</Link>
						</>
					) : (
						<>
							<div className="px-2 py-1.5">
								<Button size="sm" className="w-full" disabled>
									{t("nav.sellADisc")}
								</Button>
							</div>
							{!isLoading && (
								<div className="px-2 py-1.5">
									<Button
										variant="outline"
										size="sm"
										className="w-full"
										onClick={() => {
											close();
											openSignIn();
										}}
									>
										{t("nav.signIn")}
									</Button>
								</div>
							)}
						</>
					)}
				</nav>
				<div className="mt-auto border-t px-4 py-4">
					<div className="flex items-center justify-between">
						<span className="text-sm text-muted-foreground">
							{t("nav.theme")}
						</span>
						<div className="flex items-center gap-1">
							<LanguageToggle />
							<ThemeToggle />
						</div>
					</div>
					{isAuthenticated && (
						<Button
							variant="ghost"
							size="sm"
							className="mt-2 w-full justify-start text-destructive hover:text-destructive"
							onClick={() => {
								void signOut();
								close();
							}}
						>
							{t("nav.signOut")}
						</Button>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}

export function Navbar() {
	const { t } = useTranslation();

	return (
		<header
			className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-[14px]"
			style={{
				viewTransitionName: "site-nav",
				paddingTop: "env(safe-area-inset-top)",
			}}
		>
			<div className="page-wrap flex h-16 items-center justify-between">
				<Link
					to="/"
					className="display-title text-[26px] leading-none tracking-[0.04em] transition-opacity hover:opacity-80"
				>
					<span className="text-primary">RE:</span>DISC
				</Link>

				{/* Desktop nav */}
				<nav className="hidden items-center gap-4 md:flex">
					<Link
						to="/listings"
						search={BROWSE_SEARCH}
						className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground transition-colors hover:text-foreground"
					>
						{t("nav.browse")}
					</Link>
					<DesktopAuth />
					<LanguageToggle />
					<ThemeToggle />
				</nav>

				{/* Mobile hamburger */}
				<MobileDrawer />
			</div>
		</header>
	);
}
